import { useState, useCallback } from 'preact/hooks';
import { useGlobalConfigContext } from 'GlobalConfig.context';
import { uploadFile } from 'services/upload.services';
import { isValidFileSize, isValidFileType } from 'utils/file.utils';
import { getRandomId } from 'utils/uid.utils';
import { useTranslation } from 'react-i18next';
import { ptpCatchError } from 'services/sentry.services';
import { ErrorCodes } from 'constants/errors.constants';
import { isRuntimeError } from 'utils/fetcher.utils';
import { UploadData } from '../../../../../interfaces/ReturnRequest.interface';
import {
  ACCEPTED_FILES,
  MAX_FILE_SIZE,
  MAX_NUMBER_DOCUMENTS,
} from '../constants';

export const useUpload = (
  shipmentPk: number,
  uploadedAttachments: UploadData[] = [],
) => {
  const { t } = useTranslation();
  const { organizationId } = useGlobalConfigContext();
  const [attachments, setAttachments] =
    useState<UploadData[]>(uploadedAttachments);
  const [error, setError] = useState('');
  const acceptedFileTypes = ACCEPTED_FILES.map(({ fileType }) => fileType);

  const addAttachment = (attachment: UploadData) => {
    setAttachments((attachments) => [attachment, ...attachments]);
  };

  const updateAttachment = (id: string, payload: Partial<UploadData>) => {
    setAttachments((attachments) =>
      attachments.map((item) =>
        item.id === id ? { ...item, ...payload } : item,
      ),
    );
  };

  const handleUpload = async (evt: Event) => {
    if (!(evt.target instanceof HTMLInputElement)) return;

    const files = Array.from(evt.target.files as ArrayLike<File>);
    if (!files.length) return;

    const numOfAttchments = attachments.length + files.length;
    // prevent and show error if try to upload upload more than max number of attachments
    if (numOfAttchments > MAX_NUMBER_DOCUMENTS) {
      setError(
        `The maximum number of attachments is ${MAX_NUMBER_DOCUMENTS}. Please remove some attachments and try again.`,
      );
      return;
    }

    setError('');

    const newAttachments: UploadData[] = files.map((file: File) => ({
      id: getRandomId(),
      file,
      progress: 0,
      loading: false,
      error: '',
    }));

    newAttachments.forEach(async (attachment) => {
      // Check file type
      if (!isValidFileType(attachment.file, acceptedFileTypes)) {
        addAttachment({
          ...attachment,
          error: t('RETURN_FILE_FORMAT_NOT_SUPPORTED'),
        });
        return;
      }

      // Check file size
      if (!isValidFileSize(attachment.file, MAX_FILE_SIZE)) {
        addAttachment({
          ...attachment,
          error: t('FORM_ERROR_UPLOAD_FILE_SIZE_EXCEEDED', {
            maxFileSize: MAX_FILE_SIZE,
          }),
        });
        return;
      }

      // Upload
      addAttachment(attachment);
      const { id, file } = attachment;
      try {
        updateAttachment(id, { loading: true });
        const fileUrl = await uploadFile(file, organizationId, (progress) => {
          updateAttachment(id, { progress });
        });
        updateAttachment(id, { fileUrl });
      } catch (error) {
        if (isRuntimeError(error)) {
          ptpCatchError(error);
          updateAttachment(id, { error: ErrorCodes.GeneralError });
        }
        updateAttachment(id, { error: error.message });
      } finally {
        updateAttachment(id, { loading: false });
      }
    });
  };

  const handleRemove = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const shouldDisableUpload = attachments.length >= MAX_NUMBER_DOCUMENTS;
  const isUploading = attachments.some((item) => item.loading);
  const hasError = attachments.some((item) => item.error) || !!error;

  return {
    attachments,
    error,
    isUploading,
    hasError,
    shouldDisableUpload,
    handleUpload,
    handleRemove,
  };
};
